/* Copyright © 2026 Xerox Corporation. All Rights Reserved. Copyright protection claimed includes all forms and matters of copyrightable material and information now allowed by statutory or judicial law or hereinafter granted, including without limitation, material generated from the software programs which are displayed on the screen such as icons, screen display looks, etc. */

import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { pageSizeType } from '../components/controls/xrx-pagination.component';
@Component({
  selector: 'xacc-pagination',
  templateUrl: './xacc-pagination.component.html',
  styleUrls: ['./xacc-pagination.component.less'],
  standalone: false,
})
export class XaccPaginationComponent implements OnInit {
  @Input() page = 1;
  @Input() pageSize = 10;
  @Input() pageSizeList: pageSizeType[] | null = [];
  @Input() initialPageSize: number;
  @Input() collectionSize = 1;
  @Input() maxPageDisplayed = 3;

  @Output() pageChange = new EventEmitter<number>();
  @Output() pageSizeChange = new EventEmitter<number>();

  pageSelect: any = { page: 1 };
  pageSizeSelect: pageSizeType;
  pageList: any[];
  displayList: any = [];
  firstDisabled = true;
  leftDisabled = true;
  rightDisabled = false;
  lastDisabled = false;
  hasBoundary = false;
  currentPage = 1;
  offset = 0;
  loadingNextPage = false;
  loadingLastPage = false;

  constructor() {}

  ngOnInit(): void {
    // if there is an initial page size passed in update pageSize
    if (this.initialPageSize) {
      this.pageSize = this.initialPageSize;
    }

    // if pageSizeList isn't false update the page Size selected to match the new pageSize and send the change to the parent component
    if (this.pageSizeList !== null) {
      setTimeout(() => {
        this.pageSizeSelect = this.pageSizeList!.filter(
          (x) => x.size == this.pageSize,
        )[0];
        this.pageSizeChange.emit(this.pageSize);
      }, 0);
    }

    this.buildPageList();
    this.updateDisplayList();
  }

  // rotate the list if needed
  updateDisplayList() {
    this.displayList = [];
    const maxS = Math.min(this.maxPageDisplayed, this.pageList.length);

    if (this.pageList.length <= this.maxPageDisplayed) {
      this.pageList.forEach((p) => this.displayList.push(p));
    } else {
      for (let i = 0 + this.offset; i < maxS + this.offset; i++) {
        this.displayList.push(i + 1);
      }
    }
  }

  buildPageList() {
    this.pageList = [];
    for (let i = 0; i < Math.ceil(this.collectionSize / this.pageSize); i++) {
      this.pageList.push(i + 1);
    }
  }

  emitSelectedPage() {
    this.page = this.currentPage;
    this.pageChange.emit(this.currentPage);
    this.setButtonState();
  }

  selectPage(e: Event, page: any) {
    e.preventDefault();
    this.currentPage = page;
    this.emitSelectedPage();
  }

  nextPage(e: Event) {
    e.preventDefault();
    e.stopPropagation();
    if (this.rightDisabled) {
      return;
    }

    // open list, no boundary
    if (!this.hasBoundary && this.currentPage === this.pageList.length) {
      this.pageList.push(this.pageList.length + 1);
      this.loadingNextPage = true;
    }

    this.currentPage++;
    if (this.currentPage - this.offset > this.maxPageDisplayed) {
      this.offset++;
    }
    this.updateDisplayList();
    this.emitSelectedPage();
  }

  prevPage(e: Event) {
    e.preventDefault();
    if (this.leftDisabled) {
      return;
    }

    this.currentPage--;
    if (this.offset === this.currentPage) {
      this.offset--;
      this.updateDisplayList();
    }

    this.emitSelectedPage();
  }

  firstPage(e: Event) {
    e.preventDefault();
    if (this.firstDisabled) {
      return;
    }

    this.currentPage = 1;
    this.offset = 0;
    this.updateDisplayList();
    this.emitSelectedPage();
  }

  lastPage(e: Event) {
    e.preventDefault();
    if (this.lastDisabled) {
      return;
    }

    if (this.hasBoundary) {
      this.currentPage = this.pageList.length;
      this.offset = this.pageList.length - this.maxPageDisplayed;
      this.updateDisplayList();
    } else {
      this.hasBoundary = true;
      this.currentPage = -1; // indicate to get last page from server
      this.loadingLastPage = true;
    }
    this.emitSelectedPage();
  }

  public rebuildPageList(
    itemCount: number,
    page: number,
    hasBoundary: boolean,
  ) {
    this.hasBoundary = hasBoundary;
    this.collectionSize = itemCount;
    this.buildPageList();
    this.page = this.currentPage = page;
    if (this.page === this.pageList.length) {
      this.offset = Math.max(0, this.page - this.maxPageDisplayed);
    }
    this.loadingNextPage = false;
    this.loadingLastPage = false;
    this.updateDisplayList();
    this.setButtonState();
  }

  setButtonState() {
    this.leftDisabled = this.firstDisabled = false;
    this.rightDisabled = this.lastDisabled = false;
    if (this.page === 1) {
      this.leftDisabled = this.firstDisabled = true;
    }

    if (this.page == this.pageList.length && this.hasBoundary) {
      this.rightDisabled = this.lastDisabled = true;
    }
  }

  // act on page size select (select list)
  selectPageSize(pageSize: { size: number }): void {
    this.page = 1;
    this.pageSelect = 1;
    this.offset = 0;
    this.pageChange.emit(this.page);
    this.pageSize = pageSize.size;
    this.pageSizeChange.emit(this.pageSize);
    this.rebuildPageList(this.collectionSize, 1, this.hasBoundary);
  }
}
